<script lang="ts">
import ElTag from '@/ElTag.vue';
import ElButton from '@/ElButton.vue';
import ElIconButton from '@/ElIconButton.vue';
import ElTextCell, { ElTextCellColor, ElTextCellStyle, getTextColorClass } from '@/ElTextCell.vue';
import ElAvatar from '@/ElAvatar.vue';
import ElIcon, { ElIconProps } from '@/ElIcon.vue';
import ElInputText from '@/forms/ElInputText.vue';
import { TextualValueType } from '@/forms/input';
import ElDropdown from '@/ElDropdown.vue';
import ElTooltip from '@/ElTooltip.vue';

export const dataCellTypes = ['default', 'tag', 'action', 'input'] as const;
export type DataCellType = (typeof dataCellTypes)[number];

export type DataCell = TagCell | DefaultCell | ActionCell | InputCell;

interface BasicDataCell {
  sortableAndFilterableValue?: string;
  type?: DataCellType;
}

export interface DefaultCell extends BasicDataCell {
  type?: 'default';
  mainIcon?: ElIconProps;
  mainIconTooltip?: string;
  mainText: string;
  mainTextStyle?: ElTextCellStyle;
  mainTextColor?: ElTextCellColor;
  subIcon?: ElIconProps;
  subIconTooltip?: string;
  subText?: string;
  avatar?: Pick<InstanceType<typeof ElAvatar>['$props'], 'picture' | 'label'>;
  clickAction?: (payload?: PointerEvent) => void;
  longText?: boolean;
  truncateSubText?: boolean;
}

export interface TagCell extends BasicDataCell {
  type?: 'tag';
  tag: InstanceType<typeof ElTag>['$props'];
  subText?: string;
  truncateSubText?: boolean;
}

export interface ActionCell<ID extends string = string> extends BasicDataCell {
  type?: 'action';
  buttons?: (InstanceType<typeof ElButton>['$props'] & { id?: ID })[];
  iconButtons?: (InstanceType<typeof ElIconButton>['$props'] & { id?: ID })[];
  dropdown?: InstanceType<typeof ElDropdown>['$props'] & { disabled?: boolean };
}

export interface InputCell extends BasicDataCell {
  type?: 'input';
  value?: string;
  onUpdate?: (value: TextualValueType) => void;
}

const isCellTypeDefault = (cell: DataCell): cell is DefaultCell => !cell.type || cell.type === 'default';
const isCellTypeTag = (cell: DataCell): cell is TagCell => cell.type === 'tag';
const isCellTypeAction = (cell: DataCell): cell is ActionCell => cell.type === 'action';
const isCellTypeInput = (cell: DataCell): cell is InputCell => cell.type === 'input';

export const getCellText = (cell: DataCell) => {
  if (isCellTypeDefault(cell)) return cell.mainText;
  if (isCellTypeTag(cell)) return cell.tag.text;
  return null;
};

export const getCellSortableAndFilterableValue = (cell: DataCell): string => {
  if (cell.sortableAndFilterableValue?.length) return cell.sortableAndFilterableValue;
  if (isCellTypeDefault(cell)) return cell.mainText;
  if (isCellTypeTag(cell)) return cell.tag.text;
  if (isCellTypeInput(cell)) return cell.value || '';
  return '';
};

export const compareSortableValues = (a: string, b: string): number => {
  const numA = Number(a);
  const numB = Number(b);
  if (a !== '' && b !== '' && !isNaN(numA) && !isNaN(numB)) {
    return numA - numB;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};
</script>

<script setup lang="ts">
defineProps<{
  cell: DataCell;
}>();
</script>

<template>
  <!-- type DEFAULT -->
  <div v-if="isCellTypeDefault(cell)" :class="['flex h-12 gap-3 items-center text-nowrap', cell.clickAction && 'cursor-pointer']">
    <ElAvatar v-if="cell.avatar" size="sm" :picture="cell.avatar.picture" :label="cell.avatar.label" class="shrink-0" />

    <div class="flex flex-col gap-2 grow min-w-8">
      <div class="flex gap-2 items-center">
        <ElTooltip v-if="cell.mainIcon" :title="cell.mainIconTooltip ?? ''">
          <ElIcon
            :name="cell.mainIcon.name"
            :solid="cell.mainIcon.solid"
            :color="cell.mainIcon.color"
            class="w-4 h-4 shrink-0"
            :class="[
              !cell.mainIcon.color && cell.mainTextColor && cell.mainTextStyle !== 'underline'
                ? getTextColorClass(cell.mainTextColor, cell.mainTextStyle)
                : cell.mainIcon.color
                  ? ''
                  : 'text-neutral-darker',
              cell.mainIconTooltip ? 'cursor-pointer' : '',
            ]"
          />
        </ElTooltip>
        <div class="overflow-hidden">
          <ElTextCell
            :label="cell.mainText"
            :style="cell.mainTextStyle"
            :color-text="cell.mainTextColor"
            @click="
              () => {
                if (isCellTypeDefault(cell) && cell.clickAction) cell.clickAction();
              }
            "
          />
        </div>
      </div>
      <div v-if="cell.subIcon || cell.subText" class="flex gap-2 items-center">
        <ElTooltip v-if="cell.subIcon" :title="cell.subIconTooltip ?? ''">
          <div class="flex gap-2 items-center">
            <ElIcon
              :name="cell.subIcon.name"
              :solid="cell.subIcon.solid"
              :color="cell.subIcon.color"
              class="w-4 h-4 shrink-0"
              :class="[cell.subIconTooltip ? 'cursor-pointer' : '', cell.subIcon.color ? '' : 'text-neutral-lighter']"
            />
          </div>
        </ElTooltip>
        <div v-if="cell.subText" class="text-sm font-normal text-neutral-lighter overflow-hidden text-ellipsis">
          {{ cell.subText }}
        </div>
      </div>
    </div>
  </div>

  <!-- type TAG -->
  <div v-else-if="isCellTypeTag(cell)" class="overflow-y-hidden h-12 flex items-center">
    <div class="flex flex-col gap-2 grow min-w-8">
      <div class="text-neutral-darker text-xs inline font-normal text-nowrap">
        <ElTag v-bind="{ size: 'xs', ...cell.tag }">
          {{ cell.tag.text }}
        </ElTag>
      </div>

      <div v-if="cell.subText" class="text-sm font-normal text-neutral-lighter overflow-hidden text-ellipsis">
        {{ cell.subText }}
      </div>
    </div>
  </div>

  <!-- ACTION -->
  <div v-else-if="isCellTypeAction(cell)" class="flex items-center h-12 overflow-y-hidden content-center justify-end gap-2">
    <ElButton v-for="(button, index) in cell.buttons" :key="`btn-${index}`" v-bind="button" />
    <ElIconButton v-for="(iconButton, index) in cell.iconButtons" :key="`iconBtn-${index}`" v-bind="iconButton" />
    <ElDropdown v-if="cell.dropdown" v-bind="cell.dropdown" left>
      <template #anchor="{ toggle }">
        <ElIconButton :disabled="cell.dropdown.disabled" :icon="{ name: 'EllipsisVerticalIcon' }" @click="toggle" />
      </template>
    </ElDropdown>
  </div>

  <!-- INPUT -->
  <div v-else-if="isCellTypeInput(cell)" class="flex items-center h-12">
    <ElInputText class="w-full" :model-value="cell.value" @update:model-value="cell.onUpdate?.($event)" />
  </div>
</template>
